import { Download, UploadFile } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { Grid, Stack } from "@mui/material";
import { FileSection, FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper, DownloadHelper, StatusHelper } from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  IDownloadRequestMaterialDetail,
  IGetByIdRequest,
  IMaterialRequirement,
  IUpdateMaterialRequirementRequest,
} from "model";
import { EMaterialRequirement } from "model/Type/EMaterialRequirement";
import moment from "moment";
import React, { Fragment, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DownloadActions,
  LoadingOrderActions,
  MaterialRequirementActions,
} from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { MaterialRequirementApiService } from "services";

import { I18nTranslator, translations } from "utils/i18n";
import {
  ExportedPalletList,
  MaterialList,
  SuggestPalletList,
} from "./components";
import { EXPORT_FILE_NAME } from "utils/Const";
const _fileId = "import-receive-document";

const MaterialRequirementDetail = () => {
  const params = useParams<{
    id: string;
    code: string;
    doImportCode: string;
    description: string;
  }>();
  const fileRef = React.useRef<any>();

  const dispatch = useAppDispatch();
  const [request] = useState({ id: params.id });
  const [importType, setImportType] = useState<number>(0);
  const { loading, data = [] } = useRequest<
    IGetByIdRequest,
    IMaterialRequirement[]
  >(request, MaterialRequirementApiService.getAllMaterialRequirement);
  const [downloading, setDownloading] = useState<boolean>(false);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );

  const handleFileSelected = (files: FileList) => {
    if (files) {
      const file = files.item(0);
      DialogHelper.openConfirmImportDialog({
        title:
          importType === 0 ? "Nhập phiếu yêu cầu" : "Nhập phiếu chuyển kho",
        fileName: file?.name!,
        onConfirm: () => {
          switch (importType) {
            case 0:
              dispatch(
                MaterialRequirementActions.importMaterial.request({
                  requestMaterialId: params.id,
                  file: file as File,
                })
              );
              break;
            case 1:
              dispatch(
                MaterialRequirementActions.importMoveMaterial.request({
                  requestMaterialId: params.id,
                  file: file as File,
                })
              );
              break;
            case 2:
              dispatch(
                MaterialRequirementActions.importMoveBag.request({
                  requestMaterialId: params.id,
                  file: file as File,
                })
              );
              break;
          }
        },
      });
    }
  };

  const tabsName = [
    I18nTranslator(translations.receive.materialList),
    I18nTranslator(translations.receive.suggestionPalletList),
    I18nTranslator(translations.receive.exportPalletList),
  ];

  const tabsPanel = [
    <MaterialList id={params.id} />,
    <SuggestPalletList id={params.id} />,
    <ExportedPalletList id={params.id} />,
  ];

  const _handleDownloadExportMaterialRequisitionDetail = () => {
    console.log(params);

    const request: IDownloadRequestMaterialDetail = {
      requestMaterialId: params?.id!,
      fileName: EXPORT_FILE_NAME.requestMaterialDetail + `_[${params?.code}]`,
    };
    dispatch(DownloadActions.downloadRequestMaterialDetail.request(request));
  };

  return (
    <Fragment>
      <FileSection
        ref={fileRef}
        id={_fileId}
        onFileSelected={handleFileSelected}
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader
          loading={loading}
          data={data![0]}
          setImportType={setImportType}
        />
        <DetailTableTabs
          tabsName={tabsName}
          tabsPanel={tabsPanel}
          headerButtons={
            <Stack direction={"row"} spacing={2}>
              <LoadingButton
                startIcon={<Download />}
                variant="contained"
                onClick={_handleDownloadExportMaterialRequisitionDetail}
              >
                {I18nTranslator(
                  translations.button.exportMaterialRequisitonDetail
                )}
              </LoadingButton>
            </Stack>
          }
        />
      </div>
    </Fragment>
  );
};

const DetailHeader = (props: {
  data?: IMaterialRequirement;
  loading: boolean;
  setImportType(type: number): void;
}) => {
  const { loading, data, setImportType } = props;
  const params = useParams<{
    id: string;
    code: string;
  }>();
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );

  const [downloading, setDownloading] = useState<boolean>(false);

  const formik = useFormik<any>({
    initialValues: {
      date: data?.date || "",
      description: data?.description || "",
      status: data?.status || "N",
    },
    enableReinitialize: true,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      validateForm().then(() => {
        const request: IUpdateMaterialRequirementRequest = {
          id: data?.id!,
          date: moment(values?.date).format("MM-DD-YYYY"),
          documentNbr: data?.documentNbr!,
          insertDescription: values.description,
        };
        dispatch(
          MaterialRequirementActions.updateMaterialRequirement.request(request)
        );
      });
    },
  });

  const _handleDownloadExportMaterialRequisition = () => {
    const params: IDownloadRequestMaterialDetail = {
      requestMaterialId: data?.id!,
      fileName: EXPORT_FILE_NAME.stockMovement + `_[${data?.documentNbr}]`,
    };
    dispatch(DownloadActions.downloadStockMovement.request(params));
  };
  return (
    <ReportHeader
      title={I18nTranslator(translations.receive.documentDetail)}
      description={params.code}
      formik={formik}
      loading={
        loading ||
        action.includes(LoadingOrderActions.updateLODocument.requestName)
      }
      confirmButtonText={I18nTranslator(translations.button.save)}
      confirmButtonIcon={<SaveIcon />}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <label htmlFor={_fileId}>
            <LoadingButton
              component={"span"}
              startIcon={<UploadFile />}
              variant="contained"
              onClick={() => setImportType(0)}
            >
              {I18nTranslator(translations.button.importRequestDocs)}
            </LoadingButton>
          </label>
          <label htmlFor={_fileId}>
            <LoadingButton
              component={"span"}
              startIcon={<UploadFile />}
              variant="contained"
              onClick={() => setImportType(1)}
            >
              {I18nTranslator(translations.button.importMaterialRequisitonDocs)}
            </LoadingButton>
          </label>
          <label htmlFor={_fileId}>
            <LoadingButton
              component={"span"}
              startIcon={<UploadFile />}
              variant="contained"
              onClick={() => setImportType(2)}
            >
              {I18nTranslator(
                translations.button.importPackagingRequisitonDocs
              )}
            </LoadingButton>
          </label>
          <LoadingButton
            component={"span"}
            startIcon={<Download />}
            variant="contained"
            onClick={_handleDownloadExportMaterialRequisition}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="date"
            label={I18nTranslator(translations.masterData.palletStartDateUsed)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            label={I18nTranslator(translations.status)}
            id="status"
            disabled
            renderValue={() =>
              StatusHelper.getMaterialRequisitionStatusString(
                formik.values.status! as EMaterialRequirement
              )
            }
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.description)}
            placeholder={I18nTranslator(translations.placeholder.description)}
            id="description"
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

export default MaterialRequirementDetail;
